import { Typography, Grid } from '@mui/material';
import { directDepositSchema } from 'schemas';
import { BANK_ACCOUNT_OPTIONS } from 'enums';
import {
  useCurrentUser,
  useUpdateCompanySetupStateAsync,
  useUpdateUser,
} from 'hooks/api';
import { queryClient } from 'states/reactQueryClient';
import { FormationsTertiaryButton } from 'components/common/Buttons';
import { IAccount, ProgressTrackerStatus } from 'services/account';
import { ReadOnlyForm } from 'components/common/ReadOnlyForm';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormationsFormContainer,
  FormationsFormContent,
  FormationsFormActions,
  FormationsFormFields,
  IFormField,
  renderField,
} from 'components/common/FormationsForm2';
import { isAccount1120sOnly } from 'components/ProgressTracker/util';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import { StepInputContentContainer } from '../Components/StepInputContentContainer';

const fields: IFormField[] = [
  {
    type: FormationsFormFields.Text,
    name: 'bankName',
    label: 'Bank Name',
    options: {
      placeholder: 'Bank Name',
      autoFocus: true,
      maxLength: 80,
    },
  },
  {
    type: FormationsFormFields.Text,
    name: 'routingNumber',
    label: 'Routing Number',
    options: {
      placeholder: '123456789',
      inputMode: 'tel',
      maxLength: 9,
    },
  },
  {
    type: FormationsFormFields.Text,
    name: 'bankAccountNumber',
    label: 'Account Number',
    options: {
      placeholder: 'Account Number',
      inputMode: 'tel',
      maxLength: 15,
    },
  },
  {
    type: FormationsFormFields.Select,
    name: 'bankAccountType',
    label: 'Account Type',
    options: {
      placeholder: 'Choose an account type',
      options: BANK_ACCOUNT_OPTIONS,
    },
  },
];

export const DirectDepositInfo = ({
  handleContinue,
  currentStatus,
  account,
}: {
  handleContinue: () => void;
  currentStatus: ProgressTrackerStatus;
  account: IAccount;
}) => {
  const { t } = useTranslation();
  const { currentUser: userData } = useCurrentUser();
  const { updateCompanySetupStateAsync } = useUpdateCompanySetupStateAsync();
  const { updateUser } = useUpdateUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries(['currentUser']);
      await updateCompanySetupStateAsync({
        directDepositInformation: true,
      });
      handleContinue();
    },
  });

  const handleSubmit = async (data: any) => {
    updateUser({ data });
  };

  const is1120sOnly = useMemo(() => isAccount1120sOnly(account), [account]);
  const subtitle = useMemo(() => {
    if (currentStatus === ProgressTrackerStatus.Completed) {
      return t(
        'progressTracker.accountVerification.directDepositInfo.subTitleCompleted',
      );
    }
    return is1120sOnly
      ? t('progressTracker.accountVerification.directDepositInfo.subTitle1120s')
      : t('progressTracker.accountVerification.directDepositInfo.subTitle');
  }, [currentStatus, is1120sOnly, t]);

  const formInstance = useForm({
    defaultValues: {
      bankName: userData?.bankName,
      routingNumber: userData?.routingNumber,
      bankAccountNumber: userData?.bankAccountNumber,
      bankAccountType: userData?.bankAccountType,
    },
    resolver: yupResolver(directDepositSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  return (
    <>
      <Typography variant="h5B" component="h5">
        {t('progressTracker.accountVerification.directDepositInfo.title')}
      </Typography>
      <SubtitleText
        variant="body1"
      >
        {subtitle}
      </SubtitleText>
      <Grid container sx={{ mb: '36px' }} spacing={2}>
        {currentStatus === ProgressTrackerStatus.Completed ? (
          <Grid item xs={12}>
            <ReadOnlyForm
              items={[
                {
                  key: 'bank-name',
                  title: 'Bank Name',
                  value: userData?.bankName ?? 'N/A',
                },
                {
                  key: 'routing-number',
                  title: 'Routing Number',
                  value: userData?.routingNumber ?? 'N/A',
                },
                {
                  key: 'account-number',
                  title: 'Account Number',
                  value: userData?.bankAccountNumber ?? 'N/A',
                },
                {
                  key: 'account-type',
                  title: 'Account Type',
                  value: userData?.bankAccountType ?? 'N/A',
                },
              ]}
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <FormationsFormContainer>
              <form
                noValidate
                onSubmit={formInstance.handleSubmit(handleSubmit)}
              >
                <FormationsFormContent>
                  <StepInputContentContainer>
                    <Grid container spacing={2} marginLeft={0.25}>
                      {fields.map((field) => (
                        <Grid item key={field.name} xs={12} sx={{ mb: 1 }}>
                          {renderField(field, formInstance)}
                        </Grid>
                      ))}
                    </Grid>
                  </StepInputContentContainer>
                </FormationsFormContent>
                <FormationsFormActions>
                  <FormationsTertiaryButton
                    isLoading={formInstance.formState.isSubmitting}
                    type="submit"
                    size="large"
                    data-testid="save-direct-deposit"
                    loadingPosition="center"
                  >
                    Save and Continue
                  </FormationsTertiaryButton>
                </FormationsFormActions>
              </form>
            </FormationsFormContainer>
          </Grid>
        )}
      </Grid>
    </>
  );
};
